<template>
    <div class="grid-container-content component-spacing-top component-spacing-bottom">
        <template v-if="!error">
            <div class="col-span-full">
                <div v-if="content.showFilter" class="flex justify-end items-center w-full max-w-86 mb-2 ml-auto md:mb-8">
                    <div class="mr-2">Sort By</div>
                    <form class="flex-1 max-w-64">
                        <div class="select-input-field dropdown relative">
                            <input type="hidden" class="select-hidden-placeholder" value="Select" />
                            <select class="select-hidden-form-element hidden">
                                <option disabled="" value="" selected>Select</option>

                                <option value="option_1">Most recent first</option>

                                <option value="option_2">Oldest first</option>

                                <option value="option_3">By name</option>
                            </select>
                            <div class="toggle-select-button dropdown-toggle group flex shadow-lg rounded-t-lg rounded-b-lg cursor-pointer overflow-clip"
                                 type="button" id="dropdownSelectOption" data-bs-toggle="dropdown" data-bs-offset="0,0"
                                 aria-expanded="false">
                                <div
                                    class="selected-input w-full flex-auto py-2.5 pl-3 pr-14 border-solid border-0 border-b-1 border-b-primary text-grey-vibrant focus:outline-none focus:ring-2 focus:ring-secondary-old-900-50">
                                    Select
                                </div>
                                <div
                                    class="selected-input-button absolute right-0 flex items-center px-4 h-full bg-white border-solid border-0 border-b-1 border-b-primary rounded-t-lg rounded-br-lg cursor-pointer overflow-clip">
                                    <stockland-icon icon="chevron-down" scale="4" classes="fill-primary" />
                                    <stockland-icon icon="chevron-up" scale="4" classes="fill-primary hidden" />
                                </div>
                            </div>

                            <ul class="dropdown-menu w-full shadow-lg rounded-b-lg" aria-labelledby="dropdownSelectOption">
                                <li class="selected-option px-3 py-2.5 border-b border-grey-pastel hover:bg-info-500-12 cursor-pointer"
                                    data-value="option_1" @click="sortByRecent()">
                                    Most recent first
                                </li>

                                <li class="selected-option px-3 py-2.5 border-b border-grey-pastel hover:bg-info-500-12 cursor-pointer"
                                    data-value="option_2" @click="sortByOldest()">
                                    Oldest first
                                </li>

                                <li class="selected-option px-3 py-2.5 border-b border-grey-pastel hover:bg-info-500-12 cursor-pointer"
                                    data-value="option_3" @click="sortByName()">
                                    Name
                                </li>
                            </ul>
                        </div>
                    </form>
                </div>
                <div
                    class="grid gap-10 justify-items-center grid-cols-1 md:gap-8 md:gap-y-16 md:grid-cols-2 lg:grid-cols-3">
                    <div class="w-full bg-primary-background" v-for="article in resultList">
                        <div>
                            <div class="relative">
                                <div
                                    class="absolute top-0 left-0 inline top-auto left-auto px-6 py-1.5 md:px-8 md:py-2.5 text-brand-xs text-white bg-primary-accent font-medium">
                                    {{ article.type }}
                                </div>
                                <resized-image :data="article.image" classes="w-full" />
                            </div>

                            <div class="p-6 pb-8 text-brand-sm">
                                <div class="flex justify-between font-medium mb-5 text-primary-accent-text">
                                    <div class="flex">
                                        <stockland-icon icon="clock" scale="3"
                                                        classes="fill-transparent stroke-primary-accent mr-1.5 my-auto"></stockland-icon>
                                        {{ article.timeToRead }}
                                    </div>
                                    <div>
                                        {{ article.date }}
                                    </div>
                                </div>
                                <div class="text-brand-md font-medium text-primary-text mb-4 pb-0">
                                    {{ article.heading }}
                                </div>
                                <div class="text-brand-base text-primary-text">
                                    {{ article.body }}
                                </div>
                                <div class="flex gap-4 flex-wrap mt-14 md:mt-8">
                                    <div v-for="(button, index) in article.buttons">
                                        <stockland-button :label="button.label"
                                                          :variant="index === 0 ? 'style-2' : 'style-4'" :is-visible="true" type="link"
                                                          size="medium" :layout="index === 1 ? 'icon-right' : 'icon-none'"
                                                          :icon="index === 1 && 'download'" :href="button.link" :target="button.target"
                                                          :download="index === 1 ? true : false" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="block grid gap-7 col-span-full mt-5 mb-8 text-center md:grid-cols-3 md:gap-2">
                    <div class="flex justify-center gap-2 md:justify-start md:flex-1">
                        <div class="my-auto">Showing {{ resultList.length }} of {{ data?.totalCount ?? 0 }} results</div>
                    </div>
                    <div>
                        <stockland-button :label="content.button.label" :not-visible-label="notVisibleLabel"
                                          :is-visible="data && data.totalCount > resultList.length" :is-loading="isLoading"
                                          :type="content.button.type" :variant="content.button.variant" layout="icon-none"
                                          :size="content.button.size" @button-clicked="handleClick()" />
                    </div>
                </div>
            </div>
        </template>

        <inline-error v-if="error" />
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, reactive, toRefs, watch } from 'vue';

import { useApiGet } from '~/framework/use/api';

import { ArticleListApiResponse, ComponentData, Props as Properties, State } from './interfaces';

function useArticleListing(props: Properties) {
    const { data, error, isLoading, getData } = useApiGet<ArticleListApiResponse>();

    const state: State = reactive({
        content: props.dataContent,
        resultList: [],

        //Computed
        notVisibleLabel: computed(() => {
            if (data === null && state.resultList.length <= 0) {
                return 'We have no news to display.';
            }
            return '';
        }),
    });

    onMounted(() => {
        handleClick();
        sortByName();
        sortByRecent();
        sortByOldest();
    });

    watch(
        () => data.value,
        (newValue) => {
            state.resultList = [...state.resultList, ...(newValue?.articleList ?? [])];
        }
    );

    const sortByName = () => {
        state.resultList = state.resultList.sort((a, b) => (a.heading > b.heading ? 1 : -1));
    };

    const sortByRecent = () => {
        state.resultList = state.resultList.sort((a, b) => {
            const date1 = new Date(a.date);
            const date2 = new Date(b.date);
            return date2 - date1;
        });
    };

    const sortByOldest = () => {
        state.resultList = state.resultList.sort((a, b) => {
            const date1 = new Date(a.date);
            const date2 = new Date(b.date);
            return date1 - date2;
        });
    };

    const handleClick = () => {
        const q = state.resultList.length > 0 ? `skip=${state.resultList.length}` : '';
        const url = getQueryStringUrl(state.content.button.api, q);
        getData(url);
    };

    const getQueryStringUrl = (url: string, skipQuery: string) => {
        if (skipQuery) {
            const queryIndex = url.indexOf('?');
            if (queryIndex > -1) {
                const existingQuery = url.slice(queryIndex).indexOf('=');
                return existingQuery > -1 ? `${url}&${skipQuery}` : `${url}${skipQuery}`;
            } else {
                return `${url}?${skipQuery}`;
            }
        } else {
            return url;
        }
    };

    return {
        state,

        // Composable properties go here
        data,
        error,
        isLoading,

        //methods
        handleClick,
        sortByName,
        sortByRecent,
        sortByOldest,
    };
}

export default defineComponent({
    name: 'article-listing',
    props: {
        dataContent: {
            type: Object as PropType<ComponentData>,
            default: {},
        },
    },
    setup(props: Properties) {
        // State
        const { state, data, error, isLoading, ...methods } = useArticleListing(props);

        return {
            // Local
            ...toRefs(state),

            // Composable properties go here
            data,
            error,
            isLoading,

            ...methods,
        };
    },
});
</script>
<style lang="postcss"></style>
